import { array, boolean, object, string } from 'yup';
import { TFunction } from 'i18next';

import {
  birthNumberMatchesBirthDate,
  isForeignTaxResidence,
  isValidBirthNumber,
  isValidForeignIdentifier,
  isValidIco,
  optionalPersonNameField,
  requiredAllowedCurrencyField,
  requiredBirthDateDdMmYyyyField,
  requiredDocumentNumberField,
  requiredEmailField,
  requiredLettersOnlyField,
  requiredMaxTwoDigitNumberField,
  requiredPersonNameField,
  requiredPhoneObjectField,
  requiredPostalCodeField,
  requiredString,
} from 'common/validation';

const acceptedGoalCodes = new Set(['a', 'b', 'd', 'g', 'h', 'i', 'j', 'k']);
const blankOtherGoal = 'f';

export function makeClientFormSchema(t: TFunction) {
  return object().shape({
    gender: requiredString(t),
    firstName: requiredPersonNameField(t),
    lastName: requiredPersonNameField(t),
    email: requiredEmailField(t),
    phone: requiredPhoneObjectField(t),
    street: requiredString(t),
    streetNumber: requiredString(t),
    state: requiredString(t),
    city: requiredString(t),
    psc: requiredPostalCodeField(t),
    title: optionalPersonNameField(t),
    birthday: object()
      .shape({
        date: requiredBirthDateDdMmYyyyField(t),
        city: requiredLettersOnlyField(t),
        state: requiredLettersOnlyField(t),
      })
      .required(),
    taxId: string()
      .when('taxResidence', {
        is: isForeignTaxResidence,
        then: (schema) =>
          schema
            .required(t('form.required'))
            .test(
              'foreignTaxId',
              t('form.foreignTaxIdInvalid'),
              (value) => isValidBirthNumber(value ?? '') || isValidForeignIdentifier(value ?? ''),
            ),
        otherwise: (schema) =>
          schema
            .required(t('form.required'))
            .test('birthNumber', t('form.birthNumberInvalid'), (value) => isValidBirthNumber(value ?? '')),
      })
      .test('birthNumberDateMatch', function (value) {
        const parent = this.parent as { birthday?: { date?: string }; gender?: string };
        if (birthNumberMatchesBirthDate(value ?? '', parent.birthday?.date ?? '', parent.gender ?? '')) {
          return true;
        }
        return this.createError({ message: t('form.birthNumberDateMismatch') });
      }),
    identification: requiredString(t),
    identificationNumber: requiredDocumentNumberField(t),
    nationality: requiredString(t),
    taxResidence: requiredString(t),
    education: requiredString(t),
    status: requiredString(t),
    salary: requiredString(t),
    dependentChildren: requiredMaxTwoDigitNumberField(t),
    profession: requiredString(t),
    employerName: requiredString(t),
    employerId: string().when('taxResidence', {
      is: isForeignTaxResidence,
      then: (schema) =>
        schema
          .required(t('form.required'))
          .test(
            'foreignEmployerId',
            t('form.foreignEmployerIdInvalid'),
            (value) => isValidIco(value ?? '') || isValidForeignIdentifier(value ?? ''),
          ),
      otherwise: (schema) =>
        schema.required(t('form.required')).test('ico', t('form.icoInvalid'), (value) => isValidIco(value ?? '')),
    }),
    politicalStatus: boolean().required(t('form.required')),
    goal: array(string()).test('Required subset test', t('form.required'), (goals) =>
      (goals ?? []).some((item) => {
        const goal = item?.trim() ?? '';
        if (acceptedGoalCodes.has(goal)) {
          return true;
        }
        return goal.length > 0 && goal !== blankOtherGoal;
      }),
    ),
    currency: requiredAllowedCurrencyField(t),
  });
}
